<template>
    <Accordion
        v-bind="$attrs"
        v-model:value="accordionValue"
        class="e-collapse-accordion"
    >
        <AccordionPanel value="panel-0">
            <AccordionHeader v-if="$slots.trigger" class="tw:mb-4">
                <slot name="trigger" :open="open" :toggle="toggle" />
            </AccordionHeader>
            <AccordionContent>
                <slot />
            </AccordionContent>
        </AccordionPanel>
    </Accordion>
</template>

<script>
/**
 * ECollapse - Abstraction wrapper around PrimeVue's Accordion
 *
 * Usage:
 *   <e-collapse v-model:open="isOpen">
 *     <template #trigger>
 *       <e-button>Toggle Content</e-button>
 *     </template>
 *     <div class="content">
 *       <p>Collapsible content here</p>
 *     </div>
 *   </e-collapse>
 *
 *   <e-collapse :open="false" aria-id="contentIdForA11y">
 *     <template #trigger="props">
 *       <e-button @click="props.toggle">
 *         {{ props.open ? 'Hide' : 'Show' }} Details
 *       </e-button>
 *     </template>
 *     <div class="notification">
 *       <p>Hidden content that can be toggled</p>
 *     </div>
 *   </e-collapse>
 *
 * Props (work in both modes):
 *   - open: Boolean (default: undefined) - Controls whether the collapse is open
 *   - v-model:open: Boolean - Two-way binding for open state
 *   - aria-id: String - ID for accessibility
 *
 * Slots:
 *   - trigger: Slot for the clickable trigger element
 *     - Slot props: { open: Boolean, toggle: Function }
 *   - default: The collapsible content
 *
 * All Buefy b-collapse props, events, and slots are supported in Buefy mode.
 */
export default {
    name: 'ECollapse',
    inheritAttrs: false,
    props: {
        open: {
            type: Boolean,
            default: undefined,
        },
        modelValue: {
            type: Boolean,
            default: undefined,
        },
    },
    emits: ['update:open', 'update:modelValue'],
    computed: {
        internalOpen() {
            return this.open !== undefined ? this.open : this.modelValue;
        },
        accordionValue: {
            get() {
                // If open is true, return 'panel-0' to open the accordion
                // Otherwise return undefined to close it
                return this.internalOpen ? 'panel-0' : undefined;
            },
            set(value) {
                // If value is 'panel-0', emit true (open)
                // Otherwise emit false (closed)
                this.$emit('update:open', value === 'panel-0');
                this.$emit('update:modelValue', value === 'panel-0');
            },
        },
    },
    methods: {
        toggle() {
            this.$emit('update:open', !this.internalOpen);
            this.$emit('update:modelValue', !this.internalOpen);
        },
    },
};
</script>

<style>
/* Override PrimeVue CSS variables to disable default theming */
.e-collapse-accordion {
    --p-accordion-header-background: transparent;
    --p-accordion-header-hover-background: transparent;
    --p-accordion-header-active-background: transparent;
    --p-accordion-header-active-hover-background: transparent;
    --p-accordion-panel-background: transparent;
    --p-accordion-content-background: transparent;
    --p-accordion-header-border-color: transparent;
    --p-accordion-header-border-width: 0;
    --p-accordion-panel-border-color: transparent;
    --p-accordion-panel-border-width: 0;
    --p-accordion-header-padding: 0;
    --p-accordion-content-padding: 0;
}

/* Hide any PrimeVue icon elements (like the default toggle chevron) */
.e-collapse-accordion .p-icon {
    display: none !important;
}
</style>
